import React, { useState, useRef, useEffect, useMemo } from "react"
import ReactHtmlParser from "react-html-parser"
import * as SelectionFactoryHelper from "../helpers/SelectionFactoryHelper"
import Slider from "react-slick"
import Svg from "../_svgs/svg"
import useWindowWidth from "../../hooks/useWindowWidth"

export default function TimelineComponent({ timeline, bottomPadding, topPadding }) {
  const sliderRef = useRef(null)
  const [hasSetPosition, setHasSetPosition] = useState(false)
  const windowWidth = useWindowWidth()
  const currentYear = new Date().getFullYear().toString()

  const findClosestYearAndIndex = (yearsArray) => {
    if (yearsArray.length === 0) return null

    // Initialize closestYearObj to the first element in the array
    let closestYearObj = yearsArray[0]
    let minDiff = Math.abs(closestYearObj.year - currentYear)
    let closestYearIndex = 0

    for (let i = 1; i < yearsArray.length; i++) {
      const yearObj = yearsArray[i]
      const diff = Math.abs(parseInt(yearObj.year) - parseInt(currentYear))

      // Update closestYearObj if we find a closer year or if the difference is the same but the year is in the future
      if (diff < minDiff || (diff === minDiff && yearObj.year > closestYearObj.year)) {
        closestYearObj = yearObj
        minDiff = diff
        closestYearIndex = i
      }
    }

    return { closestYear: closestYearObj.year, closestYearIndex: closestYearIndex }
  }

  //current year in slider consists of position of element in array & a year property contained within said elements
  const [currentYearInSlider, setCurrentYearSlider] = useState(findClosestYearAndIndex(timeline))
  const tp = SelectionFactoryHelper.getFromSelectionFactory(topPadding)
  const bp = SelectionFactoryHelper.getFromSelectionFactory(bottomPadding)

  const constructNewTimeline = (timelineData) => {
    // this is done, so that we have a slide for every 2 informations instead of having one slide for every year
    let newTimeline = []
    timelineData.forEach((year) => {
      if (year.information.length <= 2) {
        newTimeline.push({
          year: year.year,
          information: year.information,
          showYear: true,
        })
      } else {
        // Split into separate objects with two information entries each
        for (let i = 0; i < year.information.length; i += 2) {
          // first range must have the year set
          if (i === 0) {
            const chunk = year.information.slice(i, i + 2)
            newTimeline.push({
              year: year.year,
              information: chunk,
              showYear: true,
            })
          } else {
            const chunk = year.information.slice(i, i + 2)
            newTimeline.push({
              year: year.year,
              information: chunk,
              showYear: false,
            })
          }
        }
      }
    })
    return newTimeline
  }

  const disableNavigationForwardIfSecondLastSlide = (constructedTimeline, currentIndex) => {
    var lastIndex = constructedTimeline.length - 1

    var nextArrow = document.querySelector("div.timeline-placeholder .slick-arrow.slick-next")
    if (nextArrow) {
      if (currentIndex + 1 === lastIndex) {
        nextArrow.classList.add("slick-disabled")
        nextArrow.setAttribute("disabled", true)
      } else {
        nextArrow.classList.remove("slick-disabled")
        nextArrow.removeAttribute("disabled")
      }
    }
  }

  const handleSliderChange = () => {
    var currentIndex = sliderRef.current.innerSlider.state.currentSlide
    var slideInfo = sliderRef.current.props.children[currentIndex].props.year
    if (slideInfo) {
      const currentYearElement = document.querySelector(".current-year")

      if (currentYearElement) {
        currentYearElement.innerHTML = slideInfo
      }

      setCurrentYearSlider({ closestYear: slideInfo, closestYearIndex: currentIndex })
      // only disable navigation to last slide if not mobile
      if (windowWidth > 768) {
        disableNavigationForwardIfSecondLastSlide(newTimeline, currentIndex)
      }
    }
  }

  useEffect(() => {
    if (windowWidth < 768 && !hasSetPosition) {
      const scrollableContainer = document.querySelector(".timeline-content")
      const yearElements = document.querySelectorAll(".timeline-year .year")
      const yearElement = Array.from(yearElements).find((el) => el.textContent === currentYearInSlider.closestYear)

      if (yearElement && scrollableContainer) {
        const containerLeft = scrollableContainer.getBoundingClientRect().left
        const yearElementLeft = yearElement.getBoundingClientRect().left

        // Get the relative left position of yearElement with respect to the scrollableContainer
        const relativeLeftPosition = yearElementLeft - containerLeft

        // Set scroll position
        scrollableContainer.scrollLeft = relativeLeftPosition - 7.5 // To ensure it's 7.5px from the viewport's left
      }

      setHasSetPosition(true)
    }
  }, [hasSetPosition, currentYearInSlider, windowWidth])

  const newTimeline = useMemo(() => constructNewTimeline(timeline), [timeline])
  const { slide: initialSlide } = useMemo(() => {
    const determineInitialSlideAndYear = (constructedTimeline, targetYear) => {
      let slide = 0
      let year = targetYear
      const currentIndex = currentYearInSlider.closestYearIndex
      if (currentIndex >= 0) {
        // If the current year is found, set it as the initial slide
        slide = currentIndex
      } else {
        // If the current year is not found, find the nearest upcoming year
        for (let i = 0; i < constructedTimeline.length; i++) {
          if (constructedTimeline[i].year > targetYear) {
            slide = i
            year = constructedTimeline[i].year
            break
          }
        }
      }
      return { slide, year }
    }
    return determineInitialSlideAndYear(newTimeline, currentYear)
  }, [newTimeline, currentYear])

  const settings = {
    dots: false,
    arrows: true,
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipe: false,
    variableWidth: true,
    centerMode: false,
    speed: 250,
    initialSlide: initialSlide,
  }

  // make sure initialSlide is set properly and navigation works
  useEffect(() => {
    if (windowWidth >= 768 && sliderRef.current && !hasSetPosition) {
      sliderRef.current.slickGoTo(initialSlide)
      setHasSetPosition(true)
    }
  }, [initialSlide, hasSetPosition, sliderRef, windowWidth])

  return (
    <div className={`timeline-block ${windowWidth < 768 ? "mobile" : "desktop"} ${tp ? tp : ""} ${bp ? bp : ""}`}>
      <div className="w--wider">
        <div className="timeline-content">
          <div className="timeline-placeholder">
            <div className="current-year-placeholder">
              <div className="current-year">{currentYearInSlider.closestYear}</div>
            </div>
            <div className="timeline-navigation"></div>
            <div className="timeline-years">
              {windowWidth < 768 ? (
                newTimeline.map((yearEntry, index) => <TimelineYearEntry key={index} {...yearEntry} />)
              ) : (
                <Slider ref={sliderRef} {...settings} afterChange={handleSliderChange}>
                  {newTimeline.map((yearEntry, index) => (
                    <TimelineYearEntry key={index} {...yearEntry} />
                  ))}
                </Slider>
              )}
            </div>
          </div>
        </div>
        <div className="mobiledrag">
          <span>Drag to navigate timeline</span>
          <Svg iconName={"draghand"} fillColor={"None"} />
        </div>
      </div>
    </div>
  )
}

export function TimelineYearEntry({ year, information, showYear }) {
  return (
    <div className={"timeline-year"}>
      {information.map((information, index) => (
        <TimelineTextEntry key={index} {...information} />
      ))}
      {showYear ? <div className="year">{year}</div> : null}
    </div>
  )
}

export function TimelineTextEntry({ placement, title, information, readMore, readMoreText, target }) {
  return (
    <div className="timeline-information-block-container">
      <div data-epi-property="Title" className="rich-text">
        {ReactHtmlParser(title)}
        <div className="information">{ReactHtmlParser(information)}</div>
      </div>
      <a target={target[0]?.value || "_blank"} href={readMore}>
        {readMoreText}
      </a>
    </div>
  )
}
